import { useEffect, useCallback, useRef } from 'preact/hooks'
import type { useWebsterChat } from '@/hooks/useWebsterChat'
import type { WebsterStreamStatusType } from '@/types/webster'

/** Pick a random item from an array */
export function pickRandom(arr: string[]): string {
    return arr[Math.floor(Math.random() * arr.length)]
}

/** Fun randomized status messages per event type */
export const STATUS_MESSAGES = {
    thinking: [
        'Thinking...',
        'Webster-ing...',
        'Pondering pixels...',
        'Brewing ideas...',
        'Channeling creativity...',
        'Contemplating layouts...',
        'Sketching mentally...',
        'Dreaming in HTML...',
        'Aligning the stars...',
        'Consulting the design gods...',
        'Drawing on the cosmic whiteboard...',
        'Running it past my mood board...',
        'Tilting my head thoughtfully...',
        'Letting the muses chime in...',
        'Doing some deep design breathing...',
        'Squinting at the possibilities...',
        'Picturing the finished page...',
        'Reading the room...',
        'Doing the design math...',
        'Asking, "what would Dieter Rams do?"...',
        'Untangling the requirements...',
        'Mulling it over...',
        'Spinning up the design engine...',
        'Calibrating taste sensors...',
        'Mapping the user journey...',
    ],
    content: [
        'Writing...',
        'Crafting your page...',
        'Painting with code...',
        'Weaving HTML...',
        'Styling things up...',
        'Making it beautiful...',
        'Assembling the pieces...',
        'Polishing the design...',
        'Laying down the markup...',
        'Choosing exactly the right shade...',
        'Tightening the spacing...',
        'Whispering CSS into being...',
        'Snapping things to a grid...',
        'Tuning the typography...',
        'Making it sing...',
        'Picking the perfect heading...',
        'Setting the tone...',
        'Tweaking the rhythm...',
        'Layering the details...',
        'Adding a little sparkle...',
    ],
    tool_use: [
        'Gathering inspiration...',
        'Doing some research...',
        'Scouting the web...',
        'Finding references...',
        'Collecting ideas...',
        'Browsing for inspo...',
        'Pulling from the knowledge base...',
        'Checking brand guidelines...',
        'Looking up the playbook...',
        'Cross-referencing the brief...',
        'Consulting the archive...',
        'Peeking at the source material...',
        'Hunting for the right asset...',
        'Sourcing the perfect example...',
        'Diving into the references...',
    ],
    starting: [
        'Warming up...',
        'Getting ready...',
        'Firing up the engines...',
        'Rolling up sleeves...',
        'Here we go...',
        'Stretching the design muscles...',
        'Booting up the studio...',
        'Cracking my knuckles...',
        'Loading the toolkit...',
        'Sharpening the pencils...',
        'Cuing the design playlist...',
        'Spinning up...',
    ],
    analyzing: [
        'Analyzing your feedback...',
        'Reading between the lines...',
        'Processing your request...',
        'Understanding your vision...',
        'Digesting that...',
        'Parsing your intent...',
        'Mapping what you mean...',
        'Lining up the implications...',
        'Decoding the request...',
        'Translating words to pixels...',
        'Connecting the dots...',
        'Working out the angle...',
        'Spotting the nuance...',
        'Sorting through the request...',
    ],
    rebuilding: [
        'Rebuilding your page...',
        'Applying changes...',
        'Tweaking the design...',
        'Making adjustments...',
        'Refining the layout...',
        'Reshuffling the deck...',
        'Reworking the markup...',
        'Recompiling the design...',
        'Rolling in the updates...',
        'Stitching the changes together...',
        'Polishing the new bits...',
        'Aligning the new pieces...',
        'Re-rendering with care...',
        'Patching things up...',
        'Threading in the changes...',
        'Working the changes in...',
        'Coaxing it into shape...',
    ],
    editing: [
        'Applying changes...',
        'Making your edits...',
        'Updating the page...',
        'Working in your feedback...',
        'Threading in the edits...',
        'Tweaking the details...',
        'Adjusting the markup...',
        'Editing in place...',
        'Nudging things into place...',
        'Patching the page...',
        'Refining the specifics...',
        'Touching up the page...',
    ],
}

export type StatusType = keyof typeof STATUS_MESSAGES

const STATUS_THROTTLE_MS = 8000

type WebsterChatInstance = ReturnType<typeof useWebsterChat>

interface UseStreamingStatusOptions {
    chat: WebsterChatInstance
}

/**
 * Manages streaming status message lifecycle: throttled status rotation,
 * auto-rotate timer during generation, and begin/finalize helpers.
 *
 * Reusable across any Webster-powered chat experience.
 */
export function useStreamingStatus({ chat }: UseStreamingStatusOptions) {
    const streamMsgId = useRef<string>('stream-0')
    const lastStatusType = useRef<string>('')
    const lastStatusUpdate = useRef<number>(0)
    /** Epoch ms when the current streaming turn (or new bubble) began. Used by the
     *  streaming status renderer to show "(15s · thinking)" alongside the rotating
     *  phrase. Set once per turn/bubble; preserved across status updates. */
    const startedAtRef = useRef<number>(0)
    const resultHandledRef = useRef(false)
    const hadRebuildRef = useRef(false)
    // When true, every status update for the active bubble renders the "editing"
    // pool regardless of the incoming event type — a targeted edit shows a
    // consistent "Applying changes…" voice instead of the rebuild/research
    // phrases. Set per-bubble by beginNewBubble; reset on a fresh turn.
    const editModeRef = useRef(false)

    const updateStatus = useCallback((type: string, messages: string[]) => {
        // Edit-mode bubbles always render the "editing" pool — the agent emits the
        // same thinking/content/tool_use events as a rebuild, but for a targeted
        // edit we want one distinct "Applying changes…" voice throughout.
        if (editModeRef.current) {
            type = 'editing'
            messages = STATUS_MESSAGES.editing
        }
        const now = Date.now()
        const typeChanged = type !== lastStatusType.current
        const throttled = now - lastStatusUpdate.current < STATUS_THROTTLE_MS

        if (!typeChanged && throttled) return

        lastStatusType.current = type
        lastStatusUpdate.current = now

        // Email asset auto-generation drives status purely through SignalR events and
        // never calls beginStreaming/beginNewBubble, so startedAt would otherwise stay 0
        // — the elapsed-time suffix wouldn't render at all. Stamp the turn start on the
        // first status event. Chat edits (sendMessage → beginStreaming) and LP flows
        // always set this beforehand, so this only fires for the auto-gen path.
        if (!startedAtRef.current) startedAtRef.current = now

        chat.updateStreamingMessage(streamMsgId.current, {
            content: '',
            streamStatus: { type: type as WebsterStreamStatusType, text: pickRandom(messages), startedAt: startedAtRef.current },
        })
    }, [chat])

    /** Prepare a new streaming message slot */
    const beginStreaming = useCallback(() => {
        const id = `stream-${Date.now()}`
        streamMsgId.current = id
        lastStatusType.current = ''
        lastStatusUpdate.current = 0
        startedAtRef.current = Date.now()
        resultHandledRef.current = false
        hadRebuildRef.current = false
        editModeRef.current = false
        chat.setGenerating(true)
        chat.updateStreamingMessage(id, {
            content: '',
            streamStatus: { type: 'thinking', text: pickRandom(STATUS_MESSAGES.starting), startedAt: startedAtRef.current },
        })
    }, [chat])

    /**
     * Open a fresh streaming slot AFTER a turn's primary response is already in the
     * chat. Used for the chat-rebuild flow: the personalized response from
     * conversationalResponse is a finalized bubble; subsequent rebuild status events
     * flow into this new bubble (which the caller removes on rebuilt).
     */
    const beginNewBubble = useCallback((initialPool: StatusType, idPrefix: string = 'bubble', editMode: boolean = false) => {
        editModeRef.current = editMode
        // A targeted edit drives the whole bubble from the "editing" pool; a full
        // rebuild uses the requested pool. LP overwrites this on the first
        // onThinking/onContent/onToolUse from the queue worker (in edit mode those
        // are coerced back to "editing" by updateStatus); email asset keeps the
        // initial pool since no further status events flow through.
        const pool: StatusType = editMode ? 'editing' : initialPool
        const id = `${idPrefix}-${Date.now()}`
        streamMsgId.current = id
        lastStatusType.current = pool
        lastStatusUpdate.current = Date.now()
        startedAtRef.current = Date.now()
        chat.setGenerating(true)
        chat.updateStreamingMessage(id, {
            content: '',
            streamStatus: { type: pool as WebsterStreamStatusType, text: pickRandom(STATUS_MESSAGES[pool]), startedAt: startedAtRef.current },
        })
        return id
    }, [chat])

    // Auto-rotate status message every 8 seconds while generating
    useEffect(() => {
        if (!chat.generating) return

        const interval = setInterval(() => {
            const type = lastStatusType.current || 'thinking'
            const pool = STATUS_MESSAGES[type as StatusType] || STATUS_MESSAGES.thinking
            chat.updateStreamingMessage(streamMsgId.current, {
                content: '',
                streamStatus: { type: type as WebsterStreamStatusType, text: pickRandom(pool), startedAt: startedAtRef.current },
            })
        }, STATUS_THROTTLE_MS)

        return () => clearInterval(interval)
    }, [chat.generating])

    return {
        streamMsgId,
        resultHandledRef,
        hadRebuildRef,
        updateStatus,
        beginStreaming,
        beginNewBubble,
    }
}
